import { Injectable } from '@angular/core';
import { AutocompleteService } from './autocomplete.service';
import { UtilsService } from './utils.service';
import { IHotsheetPropertyInfo } from '../interfaces';
import * as Parse from 'parse';
import { DocumentService } from './document.service';
import { AuthService } from './auth.service';
import { OrganizationService } from './organization.service';

@Injectable()
export class HotsheetService {

    constructor(
        private auth: AuthService,
        private ac: AutocompleteService,
        private utils: UtilsService,
        private documentService: DocumentService,
        private orgService: OrganizationService
    ) {}

    fetchPropertiesOnHotsheet() {
        const query: Parse.Query<Parse.Object> = new Parse.Query('Property');
        query.equalTo('isOnHotsheet', true);
        query.equalTo('organization', this.orgService.organization);

        if (this.auth.userType !== 'AAA') {
            query.equalTo('owner', this.auth.currentUser);
        } else if (this.auth.userType === 'AAA') {
            query.equalTo('owner', this.auth.currentUser.get('assistantOf'));
        }

        query.include('owner');
        query.include('estatedInfo');
        query.include('offerFlip');
        query.include('offerRental');
        query.include('aaFlip');
        query.include('aaRental');
        query.include('saFlip');
        query.include('saRental');
        query.include('coverPhoto');
        // q.descending('createdAt');
        query.descending('lastHotsheetImportDate');

        return query.find();
    }

    /**
     * Checks if property already exists for this user, if so will simply add the
     * property back to the user's hotsheet rather than re-creating it.
     */
    async checkIfPropertyAlreadyExists(property: IHotsheetPropertyInfo) {
        const query: Parse.Query<Parse.Object> = new Parse.Query('Property');

        query.equalTo('organization', this.orgService.organization);
        query.equalTo('owner', this.auth.currentUser);
        query.equalTo('address', property.address);
        query.equalTo('address2', property.address2);
        query.equalTo('city', property.city);
        // q.equalTo('state', property.state);
        query.equalTo('zip', property.zip);

        return query.first();
    }

    async save(original: IHotsheetPropertyInfo, fromHotsheet: boolean) {
        const property: Parse.Object = new Parse.Object('Property');

        property.set('organization', this.orgService.organization);
        property.set('status', 'imported');
        property.set('type', 'rental/flip');
        property.set('address', original.address);
        property.set('address2', original.address2);
        property.set('city', original.city);
        property.set('state', original.state);
        property.set('zip', original.zip);
        property.set('mlsId', original.mlsId);
        property.set('mlsStatus', original.mlsStatus);
        property.set('listingAgent', original.listingAgentName);
        property.set('listingOffice', original.listingOffice);
        property.set('listingAgentCell', original.listingAgentCell);
        property.set('coListingAgent', original.coListingAgentName);
        property.set('coListingAgentCell', original.coListingAgentCell);

        // we don't lock the address for imported properties to give the user the option to change the address
        // that import might have missed
        property.set('addressLocked', false);

        if (fromHotsheet) {
            property.set('isOnHotsheet', true);
            property.set('lastHotsheetImportDate', new Date());
        } else {
            property.set('isOnHotsheet', false);
        }

        const parcel = {
            standardized_land_use_category: original.standardized_land_use_category
        };

        const structure = {
            beds_count: original.beds_count,
            baths: original.baths,
            year_built: original.year_built,
            pool_type: original.pool_type
        };

        const estatedInfo: Parse.Object = new Parse.Object('EstatedInfo');
        estatedInfo.set('organization', property.get('organization'));
        estatedInfo.set('parcel', parcel);
        estatedInfo.set('structure', structure);

        // Create pointer objects
        property.set('estatedInfo', estatedInfo);
        property.get('estatedInfo').set('needToFetch', true);
        property.set('offerFlip', new Parse.Object('Offer_Flip').set('organization', property.get('organization')));
        property.set('offerRental', new Parse.Object('Offer_Rental').set('organization', property.get('organization')));
        property.set('aaFlip', new Parse.Object('AA_Flip').set('organization', property.get('organization')));
        property.set('aaRental', new Parse.Object('AA_Rental').set('organization', property.get('organization')));
        property.set('saFlip', new Parse.Object('SA_Flip').set('organization', property.get('organization')));
        property.set('saRental', new Parse.Object('SA_Rental').set('organization', property.get('organization')));
        property.set('rehabFlip', new Parse.Object('Rehab_Flip').set('organization', property.get('organization')));
        property.set('rehabRental', new Parse.Object('Rehab_Rental').set('organization', property.get('organization')));

        return property.save();
    }

    setPointersOnChildObjects(property: Parse.Object) {
        const organization: Parse.Object = property.get('organization');
        const keys: string[] = ['estatedInfo', 'offerFlip', 'offerRental', 'aaFlip', 'aaRental', 'saFlip', 'saRental', 'rehabFlip', 'rehabRental'];

        for (const key of keys) {
            const object: Parse.Object = property.get(key);

            object.set('property', property);
            // object.set('organization', organization);
        }
    }

    async getListingDocumentThumbnail(file: File) {
        if (file.type === 'application/pdf') {
            const thubmnailBase64 = await this.documentService.generatePDFThumbnail(file, 1200);

            if (thubmnailBase64) {
                return new Parse.File('thumb.png', { base64: thubmnailBase64 });
            }
        }

        return null;
    }

    notifyAssistants() {
        return Parse.Cloud.run('notifyAssistants');
    }

    async getPlaceByGoogleSearch(address: string): Promise<IHotsheetPropertyInfo> {
        return new Promise(async (resolve, reject) => {
            const property = {} as any;

            this.ac.autocomplete.getQueryPredictions({ input: address }, (results: google.maps.places.AutocompletePrediction[], status: google.maps.places.PlacesServiceStatus) => {
                if ((status === google.maps.places.PlacesServiceStatus.OK)) {
                    const result = results[0].terms;
                    let index;

                    // Google result will sometimes include street number with the address and sometimes without
                    if (result.length === 5) {
                        property.address = (result[0].value).toLowerCase();
                        index = -1;
                    } else {
                        property.address = (result[0].value + ' ' + result[1].value).toLowerCase();
                        index = 0;
                    }

                    property.city = result[index + 2].value.toLowerCase();
                    property.state = this.utils.getStateAbbreviation(result[index + 3].value).toLowerCase();
                    property.zip = result[index + 4].value.toLowerCase();

                    // If any part of the property fields includes USA then the address was not correctly parsed
                    let invalid = false;
                    for (const key in property) {
                        if (property[key] === 'USA') {
                            invalid = true;
                        }
                    }

                    if (invalid) {
                        return reject('INVALID_ADDRESS');
                    }

                    resolve(property);
                } else {
                    reject(status);
                }
            });
        });
    }
}
